/**
 * 市场数据Hook - 简化版本
 * 页面初始化时获取历史数据，然后通过WebSocket接收实时更新
 */

import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { mockMarkets } from '../data/mockMarkets';

// 生成稳定的预测和用户数据（只在初始化时生成一次）
const generateStableData = (symbol) => {
  // 使用symbol作为种子，确保相同symbol生成相同的随机数
  const safeSymbol = symbol || 'DEFAULT';
  const seed = safeSymbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const random = (seed * 9301 + 49297) % 233280 / 233280; // 简单的伪随机数生成器
  
  return {
    watchlistCount: Math.floor(200 + random * 3000),
    participantCount: Math.floor(500 + random * 4000),
    totalVolume: `$${(1000 + random * 9000).toFixed(0)}K`,
    predictions: {
      '5m': {
        up: Math.floor(45 + random * 10),
        down: Math.floor(45 + (1-random) * 10),
        vol: {
          up: `$${(800 + random * 1700).toFixed(0)}`,
          down: `$${(800 + (1-random) * 1700).toFixed(0)}`,
          total: `$${(1600 + random * 3400).toFixed(0)}`,
          count: Math.floor(50 + random * 300)
        }
      },
      '15m': {
        up: Math.floor(40 + random * 20),
        down: Math.floor(40 + (1-random) * 20),
        vol: {
          up: `$${(2000 + random * 4000).toFixed(0)}`,
          down: `$${(2000 + (1-random) * 4000).toFixed(0)}`,
          total: `$${(4000 + random * 8000).toFixed(0)}`,
          count: Math.floor(100 + random * 500)
        }
      },
      '30m': {
        up: Math.floor(35 + random * 30),
        down: Math.floor(35 + (1-random) * 30),
        vol: {
          up: `$${(5000 + random * 10000).toFixed(0)}`,
          down: `$${(5000 + (1-random) * 10000).toFixed(0)}`,
          total: `$${(10000 + random * 20000).toFixed(0)}`,
          count: Math.floor(200 + random * 800)
        }
      }
    }
  };
};

export const useMarketDataNew = () => {
  const [marketData, setMarketData] = useState([]);
  const [chartData, setChartData] = useState({});
  const [currentPrices, setCurrentPrices] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // WebSocket连接
  const { isConnected, priceData, chartData: wsChartData } = useWebSocket();

  // 页面初始化时获取历史数据
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        
        // 获取历史图表数据和当前价格
        const response = await fetch('http://localhost:8000/api/v1/chart-data');
        const result = await response.json();
        
        if (result.success) {
          const { chart_data, current_prices, symbols } = result.data;
          
          // 设置图表数据
          setChartData(chart_data);
          setCurrentPrices(current_prices);
          
          // 基于真实Redis数据构建市场数据（不使用mock数据）
          const integratedData = symbols.map(symbol => {
            const currentPrice = current_prices[symbol]?.price || 0;
            const stableData = generateStableData(symbol);
            const hasChartData = chart_data[symbol] && chart_data[symbol].length > 0;
            
            // 从mockMarkets中获取显示信息（仅用于UI显示）
            const displayInfo = mockMarkets.find(m => m.symbol === symbol) || {
              coinName: symbol.replace('USDT', ''),
              icon: '/api/placeholder/32/32',
              tags: ['Major']
            };
            
            return {
              symbol,
              coinName: displayInfo.coinName,
              name: displayInfo.coinName,
              icon: displayInfo.icon,
              tags: displayInfo.tags || ['Major'],
              currentPrice,
              priceChange: "+0.00%", // 初始化时设为0，后续通过WebSocket更新
              trend: "neutral",
              ...stableData,
              isRealtime: !!current_prices[symbol],
              hasRealChartData: hasChartData, // 标记是否有真实图表数据
              lastUpdate: current_prices[symbol]?.timestamp || Date.now()
            };
          });
          
          setMarketData(integratedData);
          setLastUpdate(Date.now());
        }
      } catch (error) {
        console.error('获取初始数据失败:', error);
        // 如果API失败，使用Mock数据
        const fallbackData = mockMarkets.map(market => ({
          ...market,
          ...generateStableData(market.symbol),
          isRealtime: false
        }));
        setMarketData(fallbackData);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // 处理WebSocket价格更新 - 应该每5秒接收一次
  useEffect(() => {
    if (!isConnected || Object.keys(priceData).length === 0) return;
    
    console.log(` [${new Date().toLocaleTimeString()}] WebSocket价格更新:`, Object.keys(priceData).length, '个交易对');

    setMarketData(prevData => 
      prevData.map(market => {
        const realtimePrice = priceData[market.symbol];
        if (realtimePrice) {
          const oldPrice = market.currentPrice;
          const newPrice = realtimePrice.price;
          const change = ((newPrice - oldPrice) / oldPrice * 100).toFixed(2);
          const trend = newPrice > oldPrice ? 'up' : newPrice < oldPrice ? 'down' : 'neutral';
          
          return {
            ...market,
            currentPrice: newPrice,
            priceChange: `${change >= 0 ? '+' : ''}${change}%`,
            trend,
            isRealtime: true,
            lastUpdate: realtimePrice.timestamp
          };
        }
        return market;
      })
    );
    
    // 更新当前价格缓存
    setCurrentPrices(prev => ({
      ...prev,
      ...priceData
    }));
    
    setLastUpdate(Date.now());
  }, [priceData, isConnected]);

  // 处理WebSocket图表数据更新 - 应该每5秒接收一次
  useEffect(() => {
    if (!isConnected || Object.keys(wsChartData).length === 0) return;
    
    // console.log(`📊 [${new Date().toLocaleTimeString()}] WebSocket图表数据更新:`, Object.keys(wsChartData).length, '个交易对');
    
    setChartData(prevChartData => {
      let hasChanges = false;
      const newChartData = { ...prevChartData }; // 浅拷贝，保持未变化交易对的引用
      
      Object.entries(wsChartData).forEach(([symbol, data]) => {
        // 检查是否真的有新数据
        const existingData = prevChartData[symbol] || [];
        const lastDataPoint = existingData[existingData.length - 1];
        
        // 如果是新的时间戳，才添加数据点
        if (!lastDataPoint || lastDataPoint.timestamp !== data.timestamp) {
          hasChanges = true;
          
          // 只为有变化的交易对创建新数组
          const updatedData = [...existingData, {
            timestamp: data.timestamp,
            price: parseFloat(data.price),
            time: new Date(data.timestamp * 1000).toLocaleTimeString()
          }];
          
          // 保持最新的360个数据点
          newChartData[symbol] = updatedData.length > 360 
            ? updatedData.slice(-360) 
            : updatedData;
        }
      });
      
      // 只有真正有变化时才返回新对象
      return hasChanges ? newChartData : prevChartData;
    });
  }, [wsChartData, isConnected]);

  // 根据标签筛选市场数据
  const getMarketsByTag = useCallback((tag) => {
    return marketData.filter(market => market.tags.includes(tag));
  }, [marketData]);

  // 获取关注列表
  const getWatchlistMarkets = useCallback(() => {
    return marketData.filter(market => market.isWatchlisted);
  }, [marketData]);

  // 统计信息
  const stats = {
    totalCount: marketData.length,
    realtimeCount: marketData.filter(m => m.isRealtime).length,
    lastUpdate: lastUpdate
  };

  return {
    marketData,
    chartData,
    currentPrices,
    isLoading,
    isConnected,
    realtimeCount: stats.realtimeCount,
    totalCount: stats.totalCount,
    stats,
    getMarketsByTag,
    getWatchlistMarkets
  };
};
